import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Loader2, HelpCircle, FileDown } from "lucide-react";

export default function PreConsultButton({ patientId }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  const openDialog = async () => {
    setOpen(true);
    if (!data) {
      const r = await api.get(`/patients/${patientId}/pre-consult/latest`);
      if (r.data && r.data.result) setData(r.data.result);
    }
  };

  const generate = async () => {
    setLoading(true);
    try {
      const { data: res } = await api.post(`/patients/${patientId}/pre-consult`);
      setData(res.result);
      toast.success("Resumo pré-consulta gerado");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const exportPdf = async () => {
    setExporting(true);
    try {
      const res = await api.get(`/patients/${patientId}/pre-consult/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error("Gere o resumo antes de exportar em PDF"); }
    finally { setExporting(false); }
  };

  const meds = data?.medicamentos || {};
  const t = data?.tendencias || {};

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" onClick={openDialog} data-testid="preconsult-btn">
          <Sparkles className="w-4 h-4 mr-2 text-ai" /> Resumo pré-consulta
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="preconsult-dialog">
        <DialogHeader>
          <DialogTitle>Resumo pré-consulta (IA de apoio)</DialogTitle>
          <DialogDescription>Preparado pela IA para orientar o médico. Decisão final é do médico.</DialogDescription>
        </DialogHeader>

        <div className="py-2 space-y-4">
          <Button onClick={generate} disabled={loading} className="w-full" data-testid="generate-preconsult-btn">
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {data ? "Regenerar resumo" : "Gerar resumo"}
          </Button>
          {data && (
            <Button variant="outline" onClick={exportPdf} disabled={exporting} className="w-full" data-testid="export-preconsult-pdf-btn">
              {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
              Exportar PDF
            </Button>
          )}

          {data && (
            <div className="space-y-4 animate-fade-up">
              {data.resumo && <p className="text-sm leading-relaxed bg-secondary/60 border-l-4 border-ai rounded-r-lg p-3">{data.resumo}</p>}

              {data.principais_alteracoes?.length > 0 && (
                <Section title="Principais alterações" items={data.principais_alteracoes} />
              )}
              {data.exames_alterados?.length > 0 && (
                <Section title="Exames alterados" items={data.exames_alterados} />
              )}
              {(meds.iniciados?.length || meds.interrompidos?.length || meds.em_uso?.length) > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Medicamentos</p>
                  <div className="flex flex-wrap gap-2">
                    {(meds.iniciados || []).map((m, i) => <Badge key={"i" + i} className="bg-success/15 text-success border-success/25" variant="outline">+ {m}</Badge>)}
                    {(meds.interrompidos || []).map((m, i) => <Badge key={"s" + i} className="bg-destructive/12 text-destructive border-destructive/25" variant="outline">− {m}</Badge>)}
                    {(meds.em_uso || []).map((m, i) => <Badge key={"u" + i} variant="secondary">{m}</Badge>)}
                  </div>
                </div>
              )}
              {(t.sono || t.atividade || t.frequencia_cardiaca || t.peso) && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Tendências</p>
                  <ul className="text-sm space-y-1">
                    {t.sono && <li><span className="font-medium">Sono:</span> {t.sono}</li>}
                    {t.atividade && <li><span className="font-medium">Atividade:</span> {t.atividade}</li>}
                    {t.frequencia_cardiaca && <li><span className="font-medium">FC:</span> {t.frequencia_cardiaca}</li>}
                    {t.peso && <li><span className="font-medium">Peso:</span> {t.peso}</li>}
                  </ul>
                </div>
              )}
              {data.sintomas?.length > 0 && <Section title="Sintomas" items={data.sintomas} />}
              {data.perguntas_sugeridas?.length > 0 && (
                <div>
                  <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">Perguntas sugeridas</p>
                  <ul className="space-y-1">
                    {data.perguntas_sugeridas.map((q, i) => (
                      <li key={i} className="text-sm flex gap-2"><HelpCircle className="w-4 h-4 text-ai shrink-0 mt-0.5" /> {q}</li>
                    ))}
                  </ul>
                </div>
              )}
              {data.disclaimer && <p className="text-xs text-muted-foreground italic border-t border-border pt-2">{data.disclaimer}</p>}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Section({ title, items }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-1">{title}</p>
      <ul className="list-disc list-inside text-sm space-y-1">
        {items.map((it, i) => <li key={i}>{it}</li>)}
      </ul>
    </div>
  );
}
